// Project time tracker.
//
// Starts a time-tracking session when a user enters a project and ends it when
// they leave the project, leave the program (tab close/refresh), or are idle
// for 15 minutes. Idle time is excluded from the total (the server ends an
// inactivity session at the last heartbeat). While active, a heartbeat advances
// the server's last_seen so a crashed session is still bounded.
//
// Records via time-tracking.php. Bearer auth is a shared apiuser, so the real
// user id is sent as user_id (backend resolves the display name).

import api from './api';

const CHAT_API_BASE = '/api/chat';
const IDLE_MS = 15 * 60 * 1000;      // 15-minute inactivity timeout
const HEARTBEAT_MS = 60 * 1000;      // advance last_seen at most once/min while active
const THROTTLE_MS = 1000;            // coalesce activity events
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];

let state = null;        // active tracking state (see startProjectTimer)
let lastHandled = 0;     // throttle marker for activity events

async function apiPost(body, extra = {}) {
  const token = (state && state.token) || (await api.ensureAuthenticated());
  return fetch(`${CHAT_API_BASE}/time-tracking.php`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
    ...extra,
  });
}

async function startSession() {
  if (!state || state.sessionId || state.starting) return;
  state.starting = true;
  try {
    const res = await apiPost({ action: 'start', project_id: state.projectId, user_id: state.userId });
    const data = await res.json();
    if (state && data && data.success && data.id) state.sessionId = data.id;
  } catch (e) { /* best effort */ }
  if (state) state.starting = false;
}

function endSessionAsync(reason, extra = {}) {
  if (!state || !state.sessionId) return;
  const id = state.sessionId;
  state.sessionId = null;
  apiPost({ action: 'end', id, reason }, extra).catch(() => {});
}

function onIdle() {
  // Inactivity timeout: end at the last heartbeat (idle excluded). A new
  // session starts automatically on the next activity while still in-project.
  endSessionAsync('inactivity');
}

function onActivity() {
  if (!state) return;
  const now = Date.now();
  state.lastActivity = now;
  state.dirty = true;
  if (now - lastHandled < THROTTLE_MS) return;
  lastHandled = now;
  clearTimeout(state.idleTimer);
  state.idleTimer = setTimeout(onIdle, IDLE_MS);
  if (!state.sessionId && !state.starting) startSession();
}

function heartbeat() {
  if (!state || !state.sessionId || !state.dirty) return;
  state.dirty = false;
  api.ensureAuthenticated().then((t) => { if (state) state.token = t; }).catch(() => {});
  apiPost({ action: 'heartbeat', id: state.sessionId }).catch(() => {});
}

// Reliable end on tab close / refresh / app close — keepalive lets the request
// finish after the page starts unloading.
function onPageHide() {
  if (state && state.sessionId) endSessionAsync('unload', { keepalive: true });
}
function onVisibility() {
  if (document.visibilityState === 'hidden') onPageHide();
}

export async function startProjectTimer(projectId, userId) {
  stopProjectTimer('switch');
  if (!projectId) return;
  state = {
    projectId, userId,
    sessionId: null, starting: false,
    lastActivity: Date.now(), dirty: true,
    idleTimer: null, hbTimer: null, token: null,
  };
  try { state.token = await api.ensureAuthenticated(); } catch (e) { /* ignore */ }
  await startSession();
  if (!state) return;
  state.idleTimer = setTimeout(onIdle, IDLE_MS);
  state.hbTimer = setInterval(heartbeat, HEARTBEAT_MS);
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibility);
}

export function stopProjectTimer(reason = 'leave') {
  if (!state) return;
  clearTimeout(state.idleTimer);
  clearInterval(state.hbTimer);
  ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
  window.removeEventListener('pagehide', onPageHide);
  document.removeEventListener('visibilitychange', onVisibility);
  endSessionAsync(reason);
  state = null;
}
